import { XMLParser, XMLValidator } from 'fast-xml-parser';

export interface SvgAttrs {
  cx?: string;
  cy?: string;
  fill?: string;
  fillOpacity?: string;
  fillRule?: string;
  height?: string;
  opacity?: string;
  points?: string;
  r?: string;
  rx?: string;
  ry?: string;
  stroke?: string;
  strokeDasharray?: string;
  strokeLinecap?: string;
  strokeLinejoin?: string;
  strokeMiterlimit?: string;
  strokeOpacity?: string;
  strokeWidth?: string;
  transform?: string;
  width?: string;
  x?: string;
  x1?: string;
  x2?: string;
  y?: string;
  y1?: string;
  y2?: string;
}

export type PdfSignatureNode =
  | { type: 'g'; attrs: SvgAttrs; children: PdfSignatureNode[] }
  | { type: 'path'; attrs: SvgAttrs & { d: string } }
  | { type: 'rect' | 'circle' | 'ellipse' | 'line' | 'polyline' | 'polygon'; attrs: SvgAttrs };

export interface PdfSignature {
  width: number;
  height: number;
  viewBox: string;
  children: PdfSignatureNode[];
}

export interface LoadPdfSignatureOptions {
  fetchFn?: typeof fetch;
}

type SignatureAttrName = keyof SvgAttrs | 'd';
type ParsedAttributes = Map<string, string>;

const SIGNATURE_PREVIEW_PATH_PATTERN = /^\/api\/sender-profiles\/[^/?#]+\/signature$/;
const SIGNATURE_PREVIEW_VERSION_PATTERN = /^\?v=\d+$/;
const NUMBER_PATTERN =
  '[-+]?(?:(?:\\d+\\.\\d+)|(?:\\d+\\.?))(?:[eE][-+]?\\d+)?|[-+]?\\.\\d+(?:[eE][-+]?\\d+)?';
const NUMBER_REGEXP = new RegExp(`^(?:${NUMBER_PATTERN})$`);
const LENGTH_REGEXP = new RegExp(`^(${NUMBER_PATTERN})(?:px)?$`, 'i');
const PATH_DATA_REGEXP = /^[MmZzLlHhVvCcSsQqTtAa0-9eE.,+\-\s]+$/;
const PATH_TOKEN_REGEXP =
  /[MmZzLlHhVvCcSsQqTtAa]|[-+]?(?:(?:\d+\.\d*)|(?:\.\d+)|(?:\d+))(?:[eE][-+]?\d+)?/y;
const XMLNS_VALUES = new Map([
  ['xmlns', 'http://www.w3.org/2000/svg'],
  ['xmlns:xlink', 'http://www.w3.org/1999/xlink'],
]);

const STYLE_ATTRIBUTES = new Set([
  'fill',
  'fill-opacity',
  'fill-rule',
  'opacity',
  'stroke',
  'stroke-dasharray',
  'stroke-linecap',
  'stroke-linejoin',
  'stroke-miterlimit',
  'stroke-opacity',
  'stroke-width',
  'transform',
]);

const ATTRIBUTE_OUTPUT_NAMES = new Map<string, SignatureAttrName>([
  ['cx', 'cx'],
  ['cy', 'cy'],
  ['d', 'd'],
  ['fill', 'fill'],
  ['fill-opacity', 'fillOpacity'],
  ['fill-rule', 'fillRule'],
  ['height', 'height'],
  ['opacity', 'opacity'],
  ['points', 'points'],
  ['r', 'r'],
  ['rx', 'rx'],
  ['ry', 'ry'],
  ['stroke', 'stroke'],
  ['stroke-dasharray', 'strokeDasharray'],
  ['stroke-linecap', 'strokeLinecap'],
  ['stroke-linejoin', 'strokeLinejoin'],
  ['stroke-miterlimit', 'strokeMiterlimit'],
  ['stroke-opacity', 'strokeOpacity'],
  ['stroke-width', 'strokeWidth'],
  ['transform', 'transform'],
  ['width', 'width'],
  ['x', 'x'],
  ['x1', 'x1'],
  ['x2', 'x2'],
  ['y', 'y'],
  ['y1', 'y1'],
  ['y2', 'y2'],
]);

const ELEMENT_ALLOWED_ATTRIBUTES = new Map<string, Set<string>>([
  ['g', new Set(STYLE_ATTRIBUTES)],
  ['path', new Set([...STYLE_ATTRIBUTES, 'd'])],
  ['rect', new Set([...STYLE_ATTRIBUTES, 'height', 'rx', 'ry', 'width', 'x', 'y'])],
  ['circle', new Set([...STYLE_ATTRIBUTES, 'cx', 'cy', 'r'])],
  ['ellipse', new Set([...STYLE_ATTRIBUTES, 'cx', 'cy', 'rx', 'ry'])],
  ['line', new Set([...STYLE_ATTRIBUTES, 'x1', 'x2', 'y1', 'y2'])],
  ['polyline', new Set([...STYLE_ATTRIBUTES, 'points'])],
  ['polygon', new Set([...STYLE_ATTRIBUTES, 'points'])],
]);

const LENGTH_ATTRIBUTES = new Set([
  'cx',
  'cy',
  'height',
  'r',
  'rx',
  'ry',
  'stroke-miterlimit',
  'stroke-width',
  'width',
  'x',
  'x1',
  'x2',
  'y',
  'y1',
  'y2',
]);

const NON_NEGATIVE_LENGTH_ATTRIBUTES = new Set([
  'height',
  'r',
  'rx',
  'ry',
  'stroke-miterlimit',
  'stroke-width',
  'width',
]);

const REQUIRED_ATTRIBUTES = new Map<string, string[]>([
  ['path', ['d']],
  ['rect', ['width', 'height']],
  ['circle', ['r']],
  ['ellipse', ['rx', 'ry']],
  ['line', ['x1', 'y1', 'x2', 'y2']],
  ['polyline', ['points']],
  ['polygon', ['points']],
]);

const parser = new XMLParser({
  ignoreAttributes: false,
  attributeNamePrefix: '@_',
  allowBooleanAttributes: false,
  parseAttributeValue: false,
  parseTagValue: false,
  preserveOrder: true,
  trimValues: false,
});

export async function loadPdfSignature(
  signaturePreviewUrl: string | null | undefined,
  options: LoadPdfSignatureOptions = {},
): Promise<PdfSignature | null> {
  const fetchUrl = normalizeSignaturePreviewUrl(signaturePreviewUrl);
  if (!fetchUrl) return null;

  const fetchFn = options.fetchFn ?? globalThis.fetch;
  if (typeof fetchFn !== 'function') return null;

  try {
    const response = await fetchFn(fetchUrl, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { accept: 'image/svg+xml' },
    });
    if (!response.ok) return null;

    const svgText = await response.text();
    return convertSignatureSvgToPdfSignature(svgText);
  } catch {
    return null;
  }
}

export function convertSignatureSvgToPdfSignature(svgText: string): PdfSignature | null {
  try {
    if (!svgText.trim()) return null;

    const validationResult = XMLValidator.validate(svgText, { allowBooleanAttributes: false });
    if (validationResult !== true) return null;

    const parsed = parser.parse(svgText) as unknown;
    if (!Array.isArray(parsed)) return null;

    const rootEntries = parsed.filter((entry) => !isWhitespaceTextEntry(entry));
    if (rootEntries.length !== 1) return null;

    const rootElement = readElement(rootEntries[0]);
    if (!rootElement || rootElement.name !== 'svg') return null;

    const dimensions = readRootDimensions(rootElement.attrs);
    if (!dimensions) return null;

    if (!validateRootAttributes(rootElement.attrs)) return null;

    const children = wrapRootPresentationAttrs(
      convertRootPresentationAttrs(rootElement.attrs),
      convertChildren(rootElement.children),
    );
    if (!children || !children.some(hasRenderableNode)) return null;

    return {
      ...dimensions,
      children,
    };
  } catch {
    return null;
  }
}

function normalizeSignaturePreviewUrl(
  signaturePreviewUrl: string | null | undefined,
): string | null {
  const trimmedUrl = signaturePreviewUrl?.trim();
  if (!trimmedUrl) return null;

  try {
    const baseUrl = new URL(globalThis.location?.href ?? 'http://localhost/');
    const parsedUrl = new URL(trimmedUrl, baseUrl);

    if (parsedUrl.origin !== baseUrl.origin) return null;
    if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') return null;
    if (!SIGNATURE_PREVIEW_PATH_PATTERN.test(parsedUrl.pathname)) return null;
    if (!SIGNATURE_PREVIEW_VERSION_PATTERN.test(parsedUrl.search)) return null;
    if (parsedUrl.hash) return null;

    return `${parsedUrl.pathname}${parsedUrl.search}`;
  } catch {
    return null;
  }
}

function readRootDimensions(
  attrs: ParsedAttributes,
): Pick<PdfSignature, 'width' | 'height' | 'viewBox'> | null {
  const viewBox = attrs.get('viewbox')?.trim();
  if (!viewBox) return null;

  const viewBoxNumbers = parseViewBox(viewBox);
  if (!viewBoxNumbers) return null;

  const widthValue = attrs.get('width')?.trim();
  const heightValue = attrs.get('height')?.trim();
  const width = widthValue ? parsePositiveLength(widthValue) : viewBoxNumbers.width;
  const height = heightValue ? parsePositiveLength(heightValue) : viewBoxNumbers.height;

  if (width === null || height === null) return null;

  return { width, height, viewBox };
}

function validateRootAttributes(attrs: ParsedAttributes): boolean {
  for (const [name, value] of attrs) {
    if (isNamespaceAttribute(name, value)) continue;
    if (name === 'version' && /^[0-9.]+$/.test(value.trim())) continue;
    if (name === 'width' || name === 'height' || name === 'viewbox') continue;
    if (STYLE_ATTRIBUTES.has(name) && normalizeAttributeValue(name, value) !== null) continue;
    return false;
  }
  return true;
}

function convertRootPresentationAttrs(attrs: ParsedAttributes): SvgAttrs | null {
  const rootAttrs: SvgAttrs = {};

  for (const [name, value] of attrs) {
    if (!STYLE_ATTRIBUTES.has(name)) continue;
    const outputName = ATTRIBUTE_OUTPUT_NAMES.get(name);
    if (!outputName || outputName === 'd') return null;

    const normalizedValue = normalizeAttributeValue(name, value);
    if (normalizedValue === null) return null;
    if (normalizedValue === '') continue;

    rootAttrs[outputName] = normalizedValue;
  }

  return rootAttrs;
}

function wrapRootPresentationAttrs(
  rootAttrs: SvgAttrs | null,
  children: PdfSignatureNode[] | null,
): PdfSignatureNode[] | null {
  if (!rootAttrs || !children) return null;
  if (Object.keys(rootAttrs).length === 0) return children;
  return [{ type: 'g', attrs: rootAttrs, children }];
}

function convertChildren(entries: unknown[]): PdfSignatureNode[] | null {
  const children: PdfSignatureNode[] = [];

  for (const entry of entries) {
    if (isWhitespaceTextEntry(entry)) continue;

    const element = readElement(entry);
    if (!element) return null;

    const converted = convertElement(element);
    if (!converted) return null;
    children.push(converted);
  }

  return children;
}

function convertElement(element: ParsedElement): PdfSignatureNode | null {
  if (element.name === 'svg') return null;

  const attrs = convertAttributes(element.name, element.attrs);
  if (!attrs) return null;

  if (element.name === 'g') {
    const children = convertChildren(element.children);
    if (!children) return null;
    return { type: 'g', attrs, children };
  }

  if (!containsOnlyWhitespaceText(element.children)) return null;
  if (!hasRequiredAttributes(element.name, attrs)) return null;

  switch (element.name) {
    case 'path':
      if (typeof attrs.d !== 'string') return null;
      return { type: 'path', attrs: attrs as SvgAttrs & { d: string } };
    case 'rect':
    case 'circle':
    case 'ellipse':
    case 'line':
    case 'polyline':
    case 'polygon':
      return { type: element.name, attrs };
    default:
      return null;
  }
}

function convertAttributes(
  elementName: string,
  attrs: ParsedAttributes,
): (SvgAttrs & Partial<{ d: string }>) | null {
  const allowedAttributes = ELEMENT_ALLOWED_ATTRIBUTES.get(elementName);
  if (!allowedAttributes) return null;

  const converted: Partial<Record<SignatureAttrName, string>> = {};

  for (const [name, value] of attrs) {
    if (isNamespaceAttribute(name, value)) continue;
    if (name.startsWith('on') || !allowedAttributes.has(name)) return null;

    const outputName = ATTRIBUTE_OUTPUT_NAMES.get(name);
    if (!outputName) return null;

    const normalizedValue = normalizeAttributeValue(name, value);
    if (normalizedValue === null) return null;
    if (normalizedValue === '') continue;

    converted[outputName] = normalizedValue;
  }

  return converted as SvgAttrs & Partial<{ d: string }>;
}

function normalizeAttributeValue(name: string, value: string): string | null {
  const trimmedValue = value.trim();
  if (!trimmedValue) return '';
  if (containsUnsafeValue(trimmedValue)) return null;

  if (LENGTH_ATTRIBUTES.has(name)) {
    return normalizeLengthValue(trimmedValue, NON_NEGATIVE_LENGTH_ATTRIBUTES.has(name));
  }

  switch (name) {
    case 'd':
      return isPathDataValue(trimmedValue) ? trimmedValue : null;
    case 'fill':
    case 'stroke':
      return isSafePaintValue(trimmedValue) ? trimmedValue : null;
    case 'fill-opacity':
    case 'opacity':
    case 'stroke-opacity':
      return isOpacityValue(trimmedValue) ? trimmedValue : null;
    case 'fill-rule':
      return trimmedValue === 'nonzero' || trimmedValue === 'evenodd' ? trimmedValue : null;
    case 'points':
      return isPointsValue(trimmedValue) ? trimmedValue : null;
    case 'stroke-dasharray':
      return isStrokeDasharrayValue(trimmedValue) ? trimmedValue : null;
    case 'stroke-linecap':
      return ['butt', 'round', 'square'].includes(trimmedValue) ? trimmedValue : null;
    case 'stroke-linejoin':
      return ['bevel', 'miter', 'round'].includes(trimmedValue) ? trimmedValue : null;
    case 'transform':
      return isTransformValue(trimmedValue) ? trimmedValue : null;
    default:
      return null;
  }
}

function normalizeLengthValue(value: string, nonNegative: boolean): string | null {
  const match = LENGTH_REGEXP.exec(value);
  if (!match?.[1]) return null;

  const numberValue = Number(match[1]);
  if (!Number.isFinite(numberValue)) return null;
  if (nonNegative && numberValue < 0) return null;

  return String(numberValue);
}

function parsePositiveLength(value: string): number | null {
  const normalized = normalizeLengthValue(value, true);
  if (normalized === null) return null;

  const numberValue = Number(normalized);
  return numberValue > 0 ? numberValue : null;
}

function parseViewBox(value: string): { width: number; height: number } | null {
  const numbers = splitNumberList(value);
  if (numbers.length !== 4) return null;

  const width = Number(numbers[2]);
  const height = Number(numbers[3]);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return null;
  }

  return { width, height };
}

function isSafePaintValue(value: string): boolean {
  if (value === 'none' || value === 'transparent' || value === 'currentColor') return true;
  if (/^#[0-9a-f]{3,8}$/i.test(value)) return true;
  if (/^[a-z]+$/i.test(value)) return true;
  return /^(?:rgb|rgba|hsl|hsla)\(\s*[-+0-9.%\s,]+\s*\)$/i.test(value);
}

function isPathDataValue(value: string): boolean {
  if (!PATH_DATA_REGEXP.test(value)) return false;
  if (/^\s*,/.test(value) || /,\s*(?:,|$)/.test(value)) return false;
  if (/,\s*[MmZzLlHhVvCcSsQqTtAa]/.test(value)) return false;

  const tokens = tokenizePathData(value);
  const firstToken = tokens?.[0];
  if (!tokens || !firstToken || !isPathCommand(firstToken) || !/[Mm]/.test(firstToken)) {
    return false;
  }

  let index = 0;
  while (index < tokens.length) {
    const command = tokens[index];
    if (!command || !isPathCommand(command)) return false;
    index += 1;

    if (/[Zz]/.test(command)) continue;

    const nextCommandIndex = tokens.findIndex((token, tokenIndex) => {
      return tokenIndex >= index && isPathCommand(token);
    });
    const numberTokens =
      nextCommandIndex === -1 ? tokens.slice(index) : tokens.slice(index, nextCommandIndex);
    if (!isValidPathCommandNumbers(command, numberTokens)) return false;
    index += numberTokens.length;
  }

  return true;
}

function tokenizePathData(value: string): string[] | null {
  const tokens: string[] = [];
  let index = 0;

  while (index < value.length) {
    const separatorMatch = /[\s,]*/y;
    separatorMatch.lastIndex = index;
    const separator = separatorMatch.exec(value);
    index = separator?.[0] ? separator.index + separator[0].length : index;
    if (index >= value.length) break;

    PATH_TOKEN_REGEXP.lastIndex = index;
    const tokenMatch = PATH_TOKEN_REGEXP.exec(value);
    if (!tokenMatch?.[0]) return null;

    tokens.push(tokenMatch[0]);
    index = PATH_TOKEN_REGEXP.lastIndex;
  }

  return tokens;
}

function isValidPathCommandNumbers(command: string, numberTokens: string[]): boolean {
  if (numberTokens.length === 0 || numberTokens.some(isPathCommand)) return false;

  switch (command) {
    case 'M':
    case 'm':
    case 'L':
    case 'l':
    case 'T':
    case 't':
      return numberTokens.length >= 2 && numberTokens.length % 2 === 0;
    case 'H':
    case 'h':
    case 'V':
    case 'v':
      return numberTokens.length >= 1;
    case 'C':
    case 'c':
      return numberTokens.length >= 6 && numberTokens.length % 6 === 0;
    case 'S':
    case 's':
    case 'Q':
    case 'q':
      return numberTokens.length >= 4 && numberTokens.length % 4 === 0;
    case 'A':
    case 'a':
      return (
        numberTokens.length >= 7 &&
        numberTokens.length % 7 === 0 &&
        numberTokens.every((token, index) => {
          const groupPosition = index % 7;
          return groupPosition === 3 || groupPosition === 4 ? token === '0' || token === '1' : true;
        })
      );
    default:
      return false;
  }
}

function isPathCommand(token: string): boolean {
  return /^[MmZzLlHhVvCcSsQqTtAa]$/.test(token);
}

function isOpacityValue(value: string): boolean {
  if (!NUMBER_REGEXP.test(value)) return false;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) && numberValue >= 0 && numberValue <= 1;
}

function isPointsValue(value: string): boolean {
  const numbers = splitNumberList(value);
  return numbers.length >= 4 && numbers.length % 2 === 0;
}

function isStrokeDasharrayValue(value: string): boolean {
  if (value === 'none') return true;

  const numbers = splitNumberList(value);
  return numbers.length > 0 && numbers.every((numberValue) => Number(numberValue) >= 0);
}

function isTransformValue(value: string): boolean {
  const transformPattern = /([A-Za-z]+)\(([^)]*)\)/g;
  let consumed = '';
  let match: RegExpExecArray | null;

  while ((match = transformPattern.exec(value)) !== null) {
    consumed += match[0];

    const functionName = match[1] ?? '';
    const rawArguments = match[2] ?? '';
    const numbers = splitNumberList(rawArguments);

    if (!isSupportedTransformArity(functionName, numbers.length)) return false;
  }

  return consumed.length > 0 && value.replace(/\s+/g, '') === consumed.replace(/\s+/g, '');
}

function isSupportedTransformArity(functionName: string, argumentCount: number): boolean {
  switch (functionName) {
    case 'matrix':
      return argumentCount === 6;
    case 'translate':
    case 'scale':
      return argumentCount === 1 || argumentCount === 2;
    case 'rotate':
      return argumentCount === 1 || argumentCount === 3;
    case 'skewX':
    case 'skewY':
      return argumentCount === 1;
    default:
      return false;
  }
}

function splitNumberList(value: string): string[] {
  if (!value.trim()) return [];

  const numbers = value
    .trim()
    .split(/[\s,]+/)
    .filter(Boolean);

  return numbers.every((numberValue) => NUMBER_REGEXP.test(numberValue)) ? numbers : [];
}

function hasRequiredAttributes(
  elementName: string,
  attrs: SvgAttrs & Partial<{ d: string }>,
): boolean {
  const requiredAttributes = REQUIRED_ATTRIBUTES.get(elementName) ?? [];
  return requiredAttributes.every((attribute) => {
    const outputName = ATTRIBUTE_OUTPUT_NAMES.get(attribute);
    return outputName !== undefined && typeof attrs[outputName] === 'string';
  });
}

function hasRenderableNode(node: PdfSignatureNode): boolean {
  return node.type === 'g' ? node.children.some(hasRenderableNode) : true;
}

function containsOnlyWhitespaceText(entries: unknown[]): boolean {
  return entries.every(isWhitespaceTextEntry);
}

interface ParsedElement {
  name: string;
  attrs: ParsedAttributes;
  children: unknown[];
}

function readElement(entry: unknown): ParsedElement | null {
  if (!isRecord(entry)) return null;

  const elementKeys = Object.keys(entry).filter((key) => key !== ':@');
  if (elementKeys.length !== 1) return null;

  const rawName = elementKeys[0];
  if (!rawName || rawName.startsWith('?') || rawName === '#text') return null;

  const name = rawName.toLowerCase();
  const allowedAttributes = name === 'svg' ? true : ELEMENT_ALLOWED_ATTRIBUTES.has(name);
  if (!allowedAttributes) return null;

  const rawChildren = entry[rawName];
  if (!Array.isArray(rawChildren)) return null;

  const attrs = readAttributes(entry[':@']);
  if (!attrs) return null;

  return { name, attrs, children: rawChildren };
}

function readAttributes(rawAttrs: unknown): ParsedAttributes | null {
  const attrs = new Map<string, string>();
  if (rawAttrs === undefined) return attrs;
  if (!isRecord(rawAttrs)) return null;

  for (const [rawName, rawValue] of Object.entries(rawAttrs)) {
    if (!rawName.startsWith('@_')) return null;

    const name = rawName.slice(2).toLowerCase();
    if (attrs.has(name)) return null;

    attrs.set(name, String(rawValue));
  }

  return attrs;
}

function isWhitespaceTextEntry(entry: unknown): boolean {
  if (!isRecord(entry)) return false;

  const keys = Object.keys(entry);
  return keys.length === 1 && keys[0] === '#text' && String(entry['#text']).trim() === '';
}

function isNamespaceAttribute(name: string, value: string): boolean {
  return XMLNS_VALUES.get(name) === value.trim();
}

function containsUnsafeValue(value: string): boolean {
  const decodedValue = decodeCssEscapes(decodeXmlEntities(value));
  const lowerValue = decodedValue.toLowerCase();
  const compactLowerValue = removeAsciiControlAndWhitespace(lowerValue);

  return (
    lowerValue.includes('javascript:') ||
    lowerValue.includes('data:') ||
    lowerValue.includes('file:') ||
    lowerValue.includes('http:') ||
    lowerValue.includes('https:') ||
    lowerValue.includes('@import') ||
    lowerValue.includes('url(') ||
    compactLowerValue.includes('javascript:') ||
    compactLowerValue.includes('data:') ||
    compactLowerValue.includes('file:') ||
    compactLowerValue.includes('http:') ||
    compactLowerValue.includes('https:') ||
    compactLowerValue.includes('@import') ||
    compactLowerValue.includes('url(')
  );
}

function decodeXmlEntities(value: string): string {
  let decoded = value;
  for (let iteration = 0; iteration < 5; iteration += 1) {
    const next = decoded
      .replace(/&#x([0-9a-f]+);/gi, (_, hex: string) =>
        String.fromCodePoint(Number.parseInt(hex, 16)),
      )
      .replace(/&#([0-9]+);/g, (_, decimal: string) =>
        String.fromCodePoint(Number.parseInt(decimal, 10)),
      )
      .replace(/&quot;/gi, '"')
      .replace(/&apos;/gi, "'")
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&amp;/gi, '&');
    if (next === decoded) return decoded;
    decoded = next;
  }
  return decoded;
}

function decodeCssEscapes(value: string): string {
  return value
    .replace(/\\([0-9a-fA-F]{1,6})\s?/g, (_, hex: string) =>
      String.fromCodePoint(Number.parseInt(hex, 16)),
    )
    .replace(/\\\r?\n/g, '')
    .replace(/\\(.)/g, '$1');
}

function removeAsciiControlAndWhitespace(value: string): string {
  return Array.from(value)
    .filter((character) => character.charCodeAt(0) > 32)
    .join('');
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
